/*
 * Pure date helpers for the convention wizard (components/convention/wizard.js).
 *
 * The browser builds every date in the user's local time zone and sends a
 * full ISO string (toISOString) so the backend never has to guess the
 * server's TZ for a "19:00" training. Nothing here touches stores or DOM.
 */

const pad2 = n => String(n).padStart(2, '0');

/** 'YYYY-MM-DD' for a local Date. */
export function localDateString(date) {
    return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
}

/** 'HH:mm' for a local Date. */
export function localTimeString(date) {
    return `${pad2(date.getHours())}:${pad2(date.getMinutes())}`;
}

/**
 * Local 'YYYY-MM-DD' + 'HH:mm' → Date in the browser's time zone, or null
 * when either part is missing or unparsable.
 */
export function localDateTime(dateStr, timeStr) {
    if (!dateStr || !timeStr) {
        return null;
    }
    const [y, m, d] = dateStr.split('-').map(Number);
    const [hh, mm] = timeStr.split(':').map(Number);
    if ([y, m, d, hh, mm].some(n => Number.isNaN(n))) {
        return null;
    }
    const date = new Date(y, m - 1, d, hh, mm, 0, 0);
    return Number.isNaN(date.getTime()) ? null : date;
}

/**
 * One {startDate, endDate} pair (ISO strings) for a single local date with
 * start/end times. An end time at or before the start time rolls the end
 * over to the next day (e.g. nattskyting 22:00 → 01:00).
 */
export function singleDateEntry({ date, startTime, endTime }) {
    const start = localDateTime(date, startTime);
    const end = localDateTime(date, endTime);
    if (!start || !end) {
        return null;
    }
    if (end <= start) {
        end.setDate(end.getDate() + 1);
    }
    return { startDate: start.toISOString(), endDate: end.toISOString() };
}

/**
 * Every date between fromDate and toDate (inclusive, local calendar days)
 * whose weekday is in `weekdays` (0 = Sunday … 6 = Saturday), each with the
 * given start/end times. Returns [] when the input cannot produce dates.
 */
export function generateRepeatDates({
    weekdays = [], fromDate, toDate, startTime, endTime,
}) {
    if (!Array.isArray(weekdays) || weekdays.length === 0 || !fromDate || !toDate || !startTime || !endTime) {
        return [];
    }
    const from = localDateTime(fromDate, '00:00');
    const to = localDateTime(toDate, '00:00');
    if (!from || !to || to < from) {
        return [];
    }
    const out = [];
    const cursor = new Date(from);
    while (cursor <= to) {
        if (weekdays.includes(cursor.getDay())) {
            const entry = singleDateEntry({ date: localDateString(cursor), startTime, endTime });
            if (entry) {
                out.push(entry);
            }
        }
        cursor.setDate(cursor.getDate() + 1);
    }
    return out;
}

/** Default title: "<klubb> - <type>", or just "<type>" without a club name. */
export function defaultTitle(customerName, presetLabel) {
    const club = (customerName || '').trim();
    return club ? `${club} - ${presetLabel}` : presetLabel;
}
